import { useState, useEffect, ChangeEvent } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { ApolloError, useMutation as useApolloMutation } from '@apollo/client';
import { useQuery as useApolloQuery } from '@apollo/client';
import {
  GET_MY_LOCATIONS_V2,
  REMOVE_LOCATION,
  GET_ALLOWED_LOCATIONS,
  UPDATE_ALLOWED_LOCATION,
  GET_LOCATION,
} from '../../../services/locationServices/queries';
import { toast } from 'react-toastify';
import {
  IGetAllowedLocationsQuery,
  IGetAllowedLocationsQueryVariables,
  ISetAllowedLocationMutationVariables,
  ISetAllowedLocationMutation,
  IGetLocationQuery,
  IGetLocationQueryVariables,
} from '../../../services/locationServices/__generated__/queries.generated';
import {
  GetMyLocationsV2LeanItem,
  GetMyLocationsV2ListItem,
  useGetMyLocationsV2Query,
  useGetMyLocationsV2LeanQuery,
} from '../../../services/locationServices/getMyLocationsV2';
import { MoonLoader } from 'react-spinners';
import { Modal } from '../../../components/modal';
import _ from 'lodash';
import { Marginer } from '../../../components/marginer';
import BaseDialog from '../../../components/dialog';
import SwitchComponent from '../../../components/switch';
import { AddOrEditLocationModal } from './components/AddOrEditLocationModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LocationsPageSkeleton from '../../../components/skeletons/LocationsPageSkeleton';
import Skeleton from '../../../components/Skeleton';
import NumericInput from '../../../components/NumericInput';
import {
  buildPageWindow,
  mergePagedItems,
  pickMatchingPageData,
  resolveValidPage,
  showingRange,
} from '../../../utils/hostListingsPagination';

const PAGE_SIZE = 20;

const Container = styled.div`
  ${tw`box-border mx-auto p-6 pb-8 md:pb-16 flex flex-col items-center justify-center w-full max-w-screen-xl`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold mb-8`}
`;

const StyledInput = styled.input`
  ${tw`w-full p-2 border border-border-default rounded-md`}
  box-sizing: border-box;

  /* Remove number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

const NextButton = styled.button<{ isActivated: boolean }>`
  ${tw`w-full py-3 rounded-lg font-semibold transition-colors border border-solid`}
  ${({ isActivated }) =>
    isActivated
      ? tw`bg-black cursor-pointer border-black text-white shadow-xs`
      : tw`bg-bg-secondary cursor-not-allowed border-border-default text-text-tertiary`}
  &:hover {
    ${({ isActivated }) =>
      isActivated ? tw`bg-black-hover cursor-pointer` : tw`bg-bg-secondary cursor-not-allowed`}
  }
`;

const ButtonContainer = styled.div`
  ${tw`flex justify-center w-full`}
`;

const LocationRow = styled.div`
  ${tw`flex justify-between items-center py-4 border-b border-border-default`}
`;

const LocationName = styled.div<{ isActive: boolean }>`
  ${tw`text-base font-bold`}
  ${({ isActive }) => (isActive ? tw`text-green-500` : tw`text-text-secondary`)}
`;

const LocationDetails = styled.div`
  ${tw`box-border flex flex-col w-full gap-4`}
`;

const InputRow = styled.div`
  ${tw`w-full`}
`;

const StyledModal = styled.div`
  ${tw`w-[600px]`}
`;

const OpHoursButton = styled.button`
  ${tw`text-black font-medium`}
  &:hover {
    ${tw`text-blue-900`}
  }
`;

// Card view components for mobile
const LocationCardContainer = styled.div`
  ${tw`box-border w-full grid grid-cols-1 gap-4 lg:hidden`}
`;

const LocationCard = styled.div`
  ${tw`bg-white rounded-lg shadow-card p-4 border border-border-default`}
`;

const CardHeader = styled.div`
  ${tw`flex justify-between items-center mb-2 pb-2 border-b border-border-default`}
`;

const CardTitle = styled.h3`
  ${tw`font-bold text-lg`}
`;

const CardRow = styled.div`
  ${tw`flex flex-col my-1`}
`;

const CardLabel = styled.span`
  ${tw`text-xs text-text-secondary uppercase font-medium`}
`;

const CardValue = styled.span`
  ${tw`text-sm`}
`;

const CardActions = styled.div`
  ${tw`flex flex-wrap justify-end gap-2 mt-4 pt-2 border-t border-border-default`}
`;

const ActionButton = styled.button`
  ${tw`px-3 py-1 text-sm rounded-md font-medium`}
`;

const EditButton = styled(ActionButton)`
  ${tw`bg-blue-100 text-black`}
  &:hover {
    ${tw`bg-blue-200`}
  }
`;

const DeleteCardButton = styled(ActionButton)`
  ${tw`bg-red-100 text-red-700`}
  &:hover {
    ${tw`bg-red-200`}
  }
`;

const OneWayButton = styled(ActionButton)`
  ${tw`bg-bg-secondary text-text-secondary`}
  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const OpHoursCardButton = styled(ActionButton)`
  ${tw`bg-green-100 text-green-700`}
  &:hover {
    ${tw`bg-green-200`}
  }
`;

export default function AddLocationPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page') || '1');
  const page = Number.isFinite(pageParam) && pageParam >= 1 ? pageParam : 1;

  const { data, loading, error, previousData, refetch } = useGetMyLocationsV2Query({
    variables: { input: { page, limit: PAGE_SIZE } },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const paged = pickMatchingPageData(page, data?.getMyLocationsV2, previousData?.getMyLocationsV2);
  const metaFallback = previousData?.getMyLocationsV2 ?? data?.getMyLocationsV2;
  const myLocations = paged?.items ?? [];
  const totalCount = paged?.totalCount ?? (loading ? (metaFallback?.totalCount ?? 0) : 0);
  const totalPages = paged?.totalPages ?? (loading ? (metaFallback?.totalPages ?? 0) : 0);
  const showInitialLoader = loading && !paged && !metaFallback;

  const [showDialog, setShowDialog] = useState(false);

  const [deleteLocation] = useApolloMutation(REMOVE_LOCATION, {
    refetchQueries: [
      { query: GET_MY_LOCATIONS_V2, variables: { input: { page, limit: PAGE_SIZE } } },
    ],
    onCompleted: () => {
      toast.success('Location deleted successfully', {
        position: 'top-center',
      });
    },
    onError: (error: ApolloError) => {
      if (error.message) {
        toast.error(error.message, {
          position: 'top-center',
          autoClose: 10000,
        });
      } else {
        toast.error('Failed to delete location');
      }
    },
  });

  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showEditOnewayFeesModal, setShowEditOnewayFeesModal] = useState(false);
  const [currentLocationId, setCurrentLocationId] = useState<number | null>(null);
  const [hasDismissedEmptyModal, setHasDismissedEmptyModal] = useState(false);

  useEffect(() => {
    if (totalCount > 0) {
      setHasDismissedEmptyModal(false);
    }
  }, [totalCount]);

  useEffect(() => {
    const response = data?.getMyLocationsV2;
    if (loading || !response || response.page !== page) return;
    const next = resolveValidPage(
      page,
      response.totalPages,
      response.items.length,
      response.totalCount,
    );
    if (next !== page) {
      setSearchParams(prev => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set('page', String(next));
        return nextParams;
      });
    }
  }, [loading, data, page, setSearchParams]);

  const setPage = (nextPage: number) => {
    setSearchParams(prev => {
      const nextParams = new URLSearchParams(prev);
      nextParams.set('page', String(nextPage));
      return nextParams;
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const range = showingRange(page, PAGE_SIZE, totalCount);
  const windowPages = buildPageWindow(page, totalPages);

  if (showInitialLoader) {
    return <LocationsPageSkeleton />;
  }
  if (error && !paged) {
    return (
      <div role="alert">
        Error loading locations: {error.message}{' '}
        <button type="button" className="underline" onClick={() => void refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const handleDeleteLocation = async () => {
    await deleteLocation({ variables: { id: Number(currentLocationId) } });
    setCurrentLocationId(null);
    setShowDialog(false);
  };

  const onEditOnewayClicked = (locationId: number) => {
    setCurrentLocationId(locationId);
    setShowEditOnewayFeesModal(true);
  };

  const showDeleteDialog = (locationId: number) => {
    setCurrentLocationId(locationId);
    setShowDialog(true);
  };

  const onCloseDialog = () => {
    setCurrentLocationId(null);
    setShowDialog(false);
  };

  const onAddLocationClicked = () => {
    setCurrentLocationId(null);
    setShowAddLocation(true);
    // Reset the dismissed state when user manually opens the modal
    setHasDismissedEmptyModal(false);
  };

  const onEditLocationClicked = (locationId: number) => {
    setCurrentLocationId(locationId);
    setShowAddLocation(true);
  };

  const handleEditOpHours = (locationId: number) => {
    navigate(`/operating-hours/${locationId}`);
  };

  const handleCloseAddLocationModal = () => {
    setShowAddLocation(false);
    if (totalCount === 0) {
      setHasDismissedEmptyModal(true);
    }
  };

  return (
    <Container>
      <AddOrEditLocationModal
        isOpen={(totalCount === 0 && !hasDismissedEmptyModal) || showAddLocation}
        locationId={currentLocationId ?? undefined}
        onClose={handleCloseAddLocationModal}
      />

      <BaseDialog
        isOpen={showDialog}
        onClose={onCloseDialog}
        title="Deleting a Location?"
        description="delete location is NOT REVERSIBLE, please make sure you really want to delete this location"
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={handleDeleteLocation}
      />
      <EditOnewayFeesModal
        fromLocationId={currentLocationId}
        showModal={showEditOnewayFeesModal}
        onClose={() => setShowEditOnewayFeesModal(false)}
        onSave={() => {}}
      />
      <Title>My Locations</Title>

      {/* Mobile Card View */}
      <LocationCardContainer>
        {myLocations?.map(location =>
          location?.id ? (
            <LocationCard
              key={`card-${location.id}`}
              data-testid={`location-card-${location.id}`}
              data-location-id={location.id}
            >
              <CardHeader>
                <CardTitle>{location.name}</CardTitle>
              </CardHeader>

              <CardRow>
                <CardLabel>Address</CardLabel>
                <CardValue>{location.address}</CardValue>
              </CardRow>

              <CardRow>
                <CardLabel>City</CardLabel>
                <CardValue>{location.city}</CardValue>
              </CardRow>

              <CardRow>
                <CardLabel>State</CardLabel>
                <CardValue>{location.state}</CardValue>
              </CardRow>

              <CardRow>
                <CardLabel>Zip</CardLabel>
                <CardValue>{location.zipCode}</CardValue>
              </CardRow>

              <CardRow>
                <CardLabel>Phone</CardLabel>
                <CardValue>{location.phoneNumber}</CardValue>
              </CardRow>

              <CardRow>
                <CardLabel>Tax Rate</CardLabel>
                <CardValue>{location.taxRate}%</CardValue>
              </CardRow>

              <CardActions>
                <OpHoursCardButton
                  onClick={() => handleEditOpHours(location.id)}
                  data-testid={`location-card-hours-${location.id}`}
                >
                  Op Hours
                </OpHoursCardButton>

                <EditButton
                  onClick={() => {
                    onEditLocationClicked(location.id);
                    setShowAddLocation(true);
                  }}
                >
                  Edit
                </EditButton>

                <DeleteCardButton onClick={() => showDeleteDialog(location.id)}>
                  Delete
                </DeleteCardButton>

                <OneWayButton onClick={() => onEditOnewayClicked(location.id)}>
                  Edit Oneway
                </OneWayButton>
              </CardActions>
            </LocationCard>
          ) : null,
        )}
      </LocationCardContainer>

      {/* Desktop Table View */}
      <TableContainer>
        <Table>
          <thead className="hidden lg:table-header-group">
            <tr>
              <Th>Name</Th>
              <Th>Address</Th>
              <Th>City</Th>
              <Th>State</Th>
              <Th>Zip</Th>
              <Th>Phone</Th>
              <Th>Tax</Th>
              <Th>Hours</Th>
              <Th>Location</Th>
              <Th>Actions</Th>
              <Th>Oneway Fees</Th>
            </tr>
          </thead>
          <tbody className="hidden lg:table-row-group">
            {myLocations?.map(location =>
              location?.id ? (
                <tr
                  key={location.id}
                  data-testid={`location-row-${location.id}`}
                  data-location-id={location.id}
                >
                  <Td>{location.name}</Td>
                  <Td>{location.address}</Td>
                  <Td>{location.city}</Td>
                  <Td>{location.state}</Td>
                  <Td>{location.zipCode}</Td>
                  <Td>{location.phoneNumber}</Td>
                  <Td>{location.taxRate}%</Td>
                  <Td>
                    <OpHoursButton
                      onClick={() => handleEditOpHours(location.id)}
                      data-testid={`location-table-hours-${location.id}`}
                    >
                      Hours
                    </OpHoursButton>
                  </Td>
                  <Td>
                    <DeleteButton
                      onClick={() => {
                        onEditLocationClicked(location.id);
                        setShowAddLocation(true);
                      }}
                    >
                      Edit
                    </DeleteButton>
                  </Td>
                  <Td>
                    <DeleteButton onClick={() => showDeleteDialog(location.id)}>
                      Delete
                    </DeleteButton>
                  </Td>
                  <Td>
                    <button onClick={() => onEditOnewayClicked(location.id)}>Edit Oneway</button>
                  </Td>
                </tr>
              ) : null,
            )}
          </tbody>
        </Table>
      </TableContainer>
      {(totalPages > 0 || page > 1) && (
        <PagerRow data-testid="my-locations-pager">
          <PagerMeta>
            {!paged
              ? `Loading page ${page}…`
              : totalCount === 0
                ? 'No locations'
                : `Showing ${range.from}–${range.to} of ${totalCount}`}
          </PagerMeta>
          <PagerControls aria-label="My locations pages">
            <PageBtn
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage(page - 1)}
              data-testid="my-locations-prev"
            >
              Prev
            </PageBtn>
            {windowPages.map((p, idx) =>
              p === 'ellipsis' ? (
                <span key={`e-${idx}`} className="px-1 text-gray-400">
                  …
                </span>
              ) : (
                <PageBtn
                  key={p}
                  type="button"
                  $active={p === page}
                  aria-current={p === page ? 'page' : undefined}
                  disabled={loading}
                  onClick={() => setPage(p)}
                  data-testid={`my-locations-page-${p}`}
                >
                  {p}
                </PageBtn>
              ),
            )}
            <PageBtn
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(page + 1)}
              data-testid="my-locations-next"
            >
              Next
            </PageBtn>
          </PagerControls>
        </PagerRow>
      )}
      <ButtonContainer>
        <Button onClick={onAddLocationClicked} data-testid="add-location-button">
          Add Location
        </Button>
      </ButtonContainer>
    </Container>
  );
}

const PagerRow = styled.div`
  ${tw`flex flex-col sm:flex-row items-center justify-between gap-3 w-full mt-6`}
`;

const PagerMeta = styled.p`
  ${tw`text-sm text-text-secondary m-0`}
`;

const PagerControls = styled.nav`
  ${tw`flex flex-wrap items-center justify-center gap-1`}
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  ${tw`min-w-[2.25rem] h-9 px-2 rounded-md border text-sm`}
  ${({ $active }) =>
    $active
      ? tw`bg-black text-white border-black`
      : tw`bg-white text-text-primary border-border-default hover:bg-bg-secondary`}
  &:disabled {
    ${tw`opacity-40 cursor-not-allowed`}
  }
`;

// Add these styled components
const TableContainer = styled.div`
  ${tw`overflow-x-auto shadow-card rounded-lg hidden lg:block`}
`;

const Table = styled.table`
  ${tw`min-w-full divide-y divide-border-default`}
`;

const Th = styled.th`
  ${tw`px-2 w-fit py-3 bg-bg-secondary text-center text-xs font-medium text-text-secondary uppercase `}
`;

const Td = styled.td`
  ${tw`px-2 w-fit py-4 whitespace-nowrap text-center text-sm text-text-primary`}
`;

const DeleteButton = styled.button`
  ${tw`text-red-600 font-medium`}
  &:hover {
    ${tw`text-red-900`}
  }
`;

const Button = styled.button`
  ${tw`mt-4 bg-black text-white font-bold py-2 px-4 rounded-md w-full max-w-md`}
  &:hover {
    ${tw`bg-blue-700`}
  }
`;

interface IEditOnewayFeesModalProps {
  fromLocationId: number | null;
  showModal: boolean;
  onClose: () => void;
  onSave: () => void;
}

const EditOnewayFeesModal = ({
  fromLocationId,
  showModal = false,
  onClose,
  onSave,
}: IEditOnewayFeesModalProps) => {
  const [peerPage, setPeerPage] = useState(1);
  const [peers, setPeers] = useState<GetMyLocationsV2LeanItem[]>([]);
  const [peersLoadMoreError, setPeersLoadMoreError] = useState(false);

  const { data: fromLocationData } = useApolloQuery<IGetLocationQuery, IGetLocationQueryVariables>(
    GET_LOCATION,
    {
      variables: { id: fromLocationId || 0 },
      skip: !showModal || fromLocationId == null,
    },
  );

  const {
    data: peersData,
    loading: peersLoading,
    error: peersError,
    refetch: refetchPeers,
  } = useGetMyLocationsV2LeanQuery({
    skip: !showModal,
    variables: { input: { page: peerPage, limit: PAGE_SIZE } },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!showModal) {
      setPeerPage(1);
      setPeers([]);
      setPeersLoadMoreError(false);
      return;
    }
    const paged = peersData?.getMyLocationsV2;
    if (!paged || paged.page !== peerPage) return;
    setPeers(prev => mergePagedItems(prev, paged.page, paged.items));
    setPeersLoadMoreError(false);
  }, [showModal, peersData, peerPage]);

  useEffect(() => {
    if (peersError && peerPage > 1) {
      setPeersLoadMoreError(true);
    }
  }, [peersError, peerPage]);

  const fromLocation = fromLocationData?.location;
  const toLocations = peers.filter(location => location.id !== fromLocationId);
  const peersHasMore =
    peersData?.getMyLocationsV2?.page === peerPage && peersData.getMyLocationsV2.hasMore === true;
  const peersLoadingMore = peersLoading && peers.length > 0;

  return (
    <StyledModal>
      <Modal isOpen={showModal} onClose={onClose}>
        <div>Edit Oneway Fees Modal</div>
        <div>
          <h4>From {fromLocation?.name}</h4>
          <h4>To Locations:</h4>
          {toLocations.map(location => (
            <div key={location.id}>
              <EditToLocationRow location={location} fromLocationId={fromLocationId} />
            </div>
          ))}
          {(peersHasMore || peersLoadMoreError) && (
            <button
              type="button"
              className="text-sm underline mt-2"
              disabled={peersLoadingMore}
              data-testid="oneway-peers-load-more"
              onClick={() => {
                if (peersLoadMoreError) {
                  setPeersLoadMoreError(false);
                  void refetchPeers();
                  return;
                }
                setPeerPage(p => p + 1);
              }}
            >
              {peersLoadingMore
                ? 'Loading…'
                : peersLoadMoreError
                  ? 'Retry load more'
                  : 'Load more locations'}
            </button>
          )}
        </div>
      </Modal>
    </StyledModal>
  );
};

const EditToLocationRow = ({
  location,
  fromLocationId,
}: {
  location: GetMyLocationsV2LeanItem | GetMyLocationsV2ListItem;
  fromLocationId: number | null;
}) => {
  const [activeLocationObj, setActiveLocationObj] = useState<any>({});
  const [fee, setFee] = useState<number>(activeLocationObj?.[location.id]?.fee ?? 0);
  const [isActive, setIsActive] = useState<boolean>(activeLocationObj?.[location.id]?.isActive);
  const { loading: allowedLocationsLoading } = useApolloQuery<
    IGetAllowedLocationsQuery,
    IGetAllowedLocationsQueryVariables
  >(GET_ALLOWED_LOCATIONS, {
    variables: { locationId: fromLocationId || 0 },
    skip: fromLocationId === null,
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const parsedData = _.keyBy(data?.getAllowedLocations, 'toLocation.id');
      setActiveLocationObj(parsedData);
      setIsActive(parsedData?.[location.id.toString()]?.isActive ?? false);
      setFee(parsedData?.[location.id.toString()]?.fee ?? 0);
    },
  });
  const buttonDisabled =
    !activeLocationObj?.[location.id]?.isActive === !isActive &&
    fee === activeLocationObj?.[location.id]?.fee;
  const [updateAllowedLocation, { loading: updateAllowedLocationLoading }] = useApolloMutation<
    ISetAllowedLocationMutation,
    ISetAllowedLocationMutationVariables
  >(UPDATE_ALLOWED_LOCATION, {
    onCompleted: data => {
      setActiveLocationObj(_.keyBy(data?.setAllowedLocation, 'toLocation.id'));
      toast.success('Oneway fee updated successfully');
    },
    onError: () => {
      toast.error('Failed to update oneway fee');
    },
  });
  if (allowedLocationsLoading) {
    return (
      <div className="flex justify-center py-4">
        <Skeleton width="100%" height="60px" />
      </div>
    );
  }
  return (
    <LocationRow key={location?.id}>
      <LocationDetails>
        <LocationRow>
          <LocationName isActive={isActive}>{location?.name}</LocationName>
          <SwitchComponent
            isActive={isActive}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setIsActive(e.target.checked)}
          />
        </LocationRow>
        <InputRow>
          <NumericInput
            value={fee}
            onValueChange={(value: number | null) => setFee(value ?? 0)}
            placeholder="Oneway Fee"
            width="100%"
          />
          <Marginer margin={20} direction="vertical" />
          <NextButton
            isActivated={!buttonDisabled || !updateAllowedLocationLoading}
            disabled={buttonDisabled || updateAllowedLocationLoading}
            onClick={() => {
              if (!fromLocationId || !location.id) return;
              updateAllowedLocation({
                variables: {
                  input: {
                    fromLocationId: fromLocationId || 0,
                    toLocationId: Number(location.id),
                    fee: fee ?? 0,
                    isActive,
                  },
                },
              });
            }}
          >
            {updateAllowedLocationLoading ? <MoonLoader color="#fff" size={10} /> : 'Save'}
          </NextButton>
        </InputRow>
      </LocationDetails>
    </LocationRow>
  );
};
